// The gate tracker (13 §Gate tracker). A pure evaluator shared by `GET /v1/operator/gate`
// (services/api) and the web app's in-memory API, so both state the same gates from the same
// observations.
//
// PLATFORM-OPERATOR: no gate is defined in code. A gate is KPI data: a metric definition of the
// platform operator's own company that carries a gate spec (`metric_def.gate`, `GateSpec`
// below) — what it measures, its condition text, its count start, its fallback target and due
// date, its kill lines. The operator's numbers are observations and target rules on those
// metrics, like any KPI. Any install defines its own gates; one with none shows an empty tracker.
//
// Inputs are the gate definitions, the live gate observations of the operator's company
// (`subject = company`) and the per-DE fan-out rows computed from the client companies (one
// company at a time; never a cross-company query). Units follow the Friday report:
// percent = fraction, currency = minor units, time = minutes.

/**
 * `incomplete` (S1-17 review #5): the population the gate is computed over was not read in full
 * (a company failed or was cut off, observations were truncated, the operator's company was not
 * readable); the value is shown, but no definitive state is claimed.
 */
export type GateState = "on_track" | "at_risk" | "missed" | "met" | "incomplete";
/**
 * Where a gate's target comes from (review #9): a persisted `target_rule` (`rule`), the gate
 * spec's own number, a documented fallback when no rule is in effect (`verdict_fallback`),
 * derived from data (`derived`: the baseline's review line) or none.
 */
export type GateTargetSource = "rule" | "verdict_fallback" | "derived" | "none";
export type GateUnit = "number" | "percent" | "currency" | "time";
export type GateDirection = "gte" | "lte" | "between";
/**
 * `sum`: cumulative since `count_from`; `mean`: average of the live values; `latest`: the newest
 * period's value; `ratio`: sum(numerator) / sum(denominator); `fanout`: the mean of the per-DE
 * rows (supervisor or founder minutes); `baseline`: latest vs a baseline period.
 */
export type GateMeasure = "sum" | "mean" | "latest" | "ratio" | "fanout" | "baseline";
export const GATE_MEASURES: readonly GateMeasure[] = [
  "sum",
  "mean",
  "latest",
  "ratio",
  "fanout",
  "baseline",
];

/** A deadline kill line on a `sum` gate: triggered when, at `due_on`, the sum is below `below`. */
export type GateKill = { text: string; due_on: string; below: number };

/**
 * A metric's gate spec (`cell.metric_def.gate`, JSON). Everything company-specific about a gate
 * lives here or in the metric's own name, unit and direction — never in code.
 */
export type GateSpec = {
  /** Display order on the tracker (ascending; ties by key). */
  order: number;
  measure: GateMeasure;
  /** The condition the gate encodes, shown with it (free text). */
  condition: string;
  /** Cumulative sums and the pacing line start on this ISO date; null = every observation. */
  count_from?: string | null | undefined;
  /** The gate's deadline when no dated target rule sets one; null = a standing threshold. */
  due_on?: string | null | undefined;
  /** The fallback target when no target rule is in effect (`verdict_fallback`). */
  target?: number | null | undefined;
  /** The fallback upper bound of a `between` gate. */
  target_max?: number | null | undefined;
  /** `sum`: the observations' notes (non-empty) become the row's note, joined by "; ". */
  notes_as_reasons?: boolean | undefined;
  /** `ratio`: the metric keys summed for the numerator and the denominator. */
  ratio?: { numerator: string; denominator: string } | undefined;
  /** `fanout`: which per-DE minutes the mean is over. */
  fanout?: "supervision" | "founder" | undefined;
  /**
   * `baseline`: the baseline period (`period_key`, starting `starts_on`), the review and halt
   * lines as fractions of it (latest ≤ review_at: at risk; ≤ halt_at: missed), the kill texts
   * shown for each, and the label used in notes ("of the <label>").
   */
  baseline?:
    | {
        period_key: string;
        starts_on: string;
        label: string;
        review_at: number;
        halt_at: number;
        review_text?: string | null | undefined;
        halt_text?: string | null | undefined;
      }
    | undefined;
  /** Row notes: `none` when nothing is logged, `below` / `above` when out of the target. */
  notes?:
    | {
        none?: string | null | undefined;
        below?: string | null | undefined;
        above?: string | null | undefined;
      }
    | undefined;
  /** Deadline kill lines (`sum` gates). */
  kill?: GateKill[] | undefined;
};

/** One gate: its metric's key, name, unit and direction, plus its spec. */
export type GateDef = {
  key: string;
  label: string;
  unit: GateUnit;
  direction: GateDirection;
  spec: GateSpec;
};

/** One live observation of a gate metric on the operator's company. */
export type GateObservation = {
  key: string;
  period_key: string;
  /** First day of the observation's period (`period_starts_on`). */
  starts_on: string;
  value: number | null;
  note: string | null;
};

export type GatePerDe = {
  company: { id: string; name: string };
  de: { membership_id: string; display_name: string };
  supervisor_minutes_week: number;
  founder_minutes_week: number;
  day: number;
};

export type GateRow = {
  key: string;
  label: string;
  unit: GateUnit;
  direction: GateDirection;
  measure: GateMeasure;
  condition: string;
  /** `fanout` gates: which per-DE minutes; else null. */
  fanout: "supervision" | "founder" | null;
  actual: number | null;
  target: number | null;
  due_on: string | null;
  state: GateState;
  days_left: number | null;
  note: string | null;
  /** The upper bound of a `between` gate, else null. */
  target_max: number | null;
  target_source: GateTargetSource;
};

export type GatePayload = {
  as_of: string;
  /** The operator's company the gates are defined on; null when none is marked or readable. */
  company: { id: string; slug: string; name: string } | null;
  /** False when any input population was not read in full (`incomplete_reasons` say which). */
  complete: boolean;
  incomplete_reasons: string[];
  gates: GateRow[];
  /** `triggered` is null when the observations it needs are incomplete. */
  kill_criteria: { text: string; triggered: boolean | null }[];
  per_de: GatePerDe[];
};

/** A persisted target (`target_rule`, subject = company, in effect on `as_of`). */
/** `due_on`: the rule's own date for a dated gate (its `effective_to`), overriding the spec's. */
export type GateTarget = { value: number; value_max: number | null; due_on?: string | null };

/** Fan-out totals over every active DE seat read (not only the `per_de` rows shown). */
export type GateFanout = { des: number; supervisor_minutes: number; founder_minutes: number };

const DAY_MS = 86_400_000;
const dayNumber = (iso: string) => Math.floor(Date.parse(`${iso}T00:00:00Z`) / DAY_MS);

/** Calendar days from `from` to `to` (both ISO dates). */
export function isoDaysBetween(from: string, to: string): number {
  return dayNumber(to) - dayNumber(from);
}

/** The DE's day counter on `asOf`: day 1 is `started_on`; 0 before it or without a start. */
export function deDay(startedOn: string | null, asOf: string): number {
  if (!startedOn) return 0;
  return Math.max(0, isoDaysBetween(startedOn, asOf) + 1);
}

function mean(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((s, v) => s + v, 0) / values.length;
}

/** Gate definitions in display order (spec `order`, then key). */
export function orderGateDefs(defs: readonly GateDef[]): GateDef[] {
  return [...defs].sort((a, b) => a.spec.order - b.spec.order || a.key.localeCompare(b.key));
}

/** The metric keys whose observations the gates read (everything but the fan-out). */
export function gateObservedKeys(defs: readonly GateDef[]): string[] {
  const keys = new Set<string>();
  for (const d of defs) {
    if (d.spec.measure === "ratio" && d.spec.ratio) {
      keys.add(d.spec.ratio.numerator);
      keys.add(d.spec.ratio.denominator);
    } else if (d.spec.measure !== "fanout") keys.add(d.key);
  }
  return [...keys].sort();
}

/**
 * The first day the gates read: the earliest count start or baseline period; null = no lower
 * bound (a gate without a start — a `latest` gate's newest value may be older than any count
 * start). The read stays bounded by `GATE_LIMITS.observations`.
 */
export function gateObservationFrom(defs: readonly GateDef[]): string | null {
  let from: string | null = null;
  for (const d of defs) {
    if (d.spec.measure === "fanout") continue;
    const start = d.spec.measure === "baseline" ? d.spec.baseline?.starts_on : d.spec.count_from;
    if (!start) return null;
    if (from === null || start < from) from = start;
  }
  return from;
}

/** Pacing state of a `gte` cumulative gate with a due date (linear from `from`). */
function paceState(
  actual: number,
  target: number,
  due: string,
  asOf: string,
  from: string | null,
): GateState {
  if (actual >= target) return "met";
  if (asOf > due) return "missed";
  if (!from) return "on_track";
  const total = Math.max(1, isoDaysBetween(from, due));
  const elapsed = Math.min(total, Math.max(0, isoDaysBetween(from, asOf)));
  return actual >= (target * elapsed) / total ? "on_track" : "at_risk";
}

const pct = (fraction: number) => `${Math.round(fraction * 100)}%`;
const fill = (text: string, vars: Record<string, string | number>) =>
  text.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));

/**
 * Evaluates every gate on `asOf` (an ISO date in the operator company's calendar).
 * `observations` must already be the live rows (one per cell); rows after `asOf` are ignored,
 * cumulative sums start on each gate's `count_from`. `perDe` are the fan-out rows shown;
 * `fanout` the totals over every active DE seat read (defaults to the totals of `perDe`).
 * `targets` are the persisted rules by gate key; a gate without one falls back to its spec's
 * number, flagged `verdict_fallback`. `incomplete` names an input that was not read in full: its
 * gates are `incomplete`.
 */
export function evaluateGate(input: {
  asOf: string;
  defs: readonly GateDef[];
  company?: GatePayload["company"];
  observations: readonly GateObservation[];
  perDe: readonly GatePerDe[];
  fanout?: GateFanout;
  targets?: ReadonlyMap<string, GateTarget>;
  incomplete?: { observations?: string | null; fanout?: string | null };
}): GatePayload {
  const { asOf } = input;
  const defs = orderGateDefs(input.defs);
  const obsGap = input.incomplete?.observations ?? null;
  const fanGap = input.incomplete?.fanout ?? null;
  const fanout: GateFanout = input.fanout ?? {
    des: input.perDe.length,
    supervisor_minutes: input.perDe.reduce((s, d) => s + d.supervisor_minutes_week, 0),
    founder_minutes: input.perDe.reduce((s, d) => s + d.founder_minutes_week, 0),
  };
  const seen = input.observations.filter((o) => o.starts_on <= asOf);
  const valuesOf = (key: string, from: string | null, through = asOf) =>
    seen
      .filter((o) => o.key === key && (from === null || o.starts_on >= from))
      .filter((o) => o.starts_on <= through && o.value !== null)
      .sort((a, b) => a.starts_on.localeCompare(b.starts_on));
  const sum = (key: string, from: string | null, through = asOf) =>
    valuesOf(key, from, through).reduce((s, o) => s + (o.value ?? 0), 0);

  /** The baseline gate's ratio of the latest value to the baseline period's. */
  const baselineOf = (def: GateDef) => {
    const b = def.spec.baseline;
    const all = valuesOf(def.key, null);
    const base = b ? (all.find((o) => o.period_key === b.period_key)?.value ?? null) : null;
    const latest = all.at(-1)?.value ?? null;
    const ratio = base !== null && base > 0 && latest !== null ? latest / base : null;
    return { base, latest, ratio };
  };

  const gates = defs.map((def): GateRow => {
    const spec = def.spec;
    const from = spec.count_from ?? null;
    const specTarget = spec.target ?? null;
    const specDue = spec.due_on ?? null;
    const rule = input.targets?.get(def.key);
    const dueOn = specDue === null ? null : (rule?.due_on ?? specDue);
    const daysLeft = dueOn === null ? null : Math.max(0, isoDaysBetween(asOf, dueOn));
    const target = rule ? rule.value : specTarget;
    const targetMax = rule ? (rule.value_max ?? undefined) : (spec.target_max ?? undefined);
    const source: GateTargetSource = rule
      ? "rule"
      : specTarget !== null
        ? "verdict_fallback"
        : spec.measure === "baseline"
          ? "derived"
          : "none";
    const gap = spec.measure === "fanout" ? fanGap : obsGap;
    const row = (
      actual: number | null,
      state: GateState,
      note: string | null,
      shownTarget = target,
    ): GateRow => ({
      key: def.key,
      label: def.label,
      unit: def.unit,
      direction: def.direction,
      measure: spec.measure,
      condition: spec.condition,
      fanout: spec.measure === "fanout" ? (spec.fanout ?? "supervision") : null,
      actual,
      target: shownTarget,
      due_on: dueOn,
      // An incomplete population never yields a definitive state (review #5).
      state: gap ? "incomplete" : state,
      days_left: daysLeft,
      note: gap ? [note, `Incomplete: ${gap}`].filter(Boolean).join("; ") : note,
      target_max: def.direction === "between" ? (targetMax ?? null) : null,
      target_source: source,
    });
    const notes = spec.notes ?? {};
    /** The metric's own direction decides which side of the target is good. */
    const meets = (v: number, t: number) => (def.direction === "lte" ? v <= t : v >= t);

    switch (spec.measure) {
      case "sum": {
        const actual = sum(def.key, from);
        if (spec.notes_as_reasons) {
          const reasons = valuesOf(def.key, from)
            .map((o) => o.note?.trim())
            .filter((n): n is string => !!n);
          return row(actual, "on_track", reasons.length ? reasons.join("; ") : null);
        }
        if (target === null) return row(actual, "on_track", null);
        if (def.direction === "lte")
          return row(actual, actual <= target ? "on_track" : "missed", null);
        if (dueOn) {
          // After the due date the state is what stood on the due date (review #10).
          const byDue = sum(def.key, from, dueOn < asOf ? dueOn : asOf);
          return row(actual, paceState(byDue, target, dueOn, asOf, from), null);
        }
        return row(actual, actual >= target ? "met" : "on_track", null);
      }
      case "ratio": {
        const r = spec.ratio;
        const denominator = r ? sum(r.denominator, from) : 0;
        if (!r || denominator === 0)
          return row(null, "at_risk", notes.none ?? "Nothing logged yet");
        const rate = sum(r.numerator, from) / denominator;
        const min = target ?? 0;
        // A ratio is a fraction: without an upper bound it is capped at 100 % (as before).
        const max = targetMax ?? 1;
        if (rate < min) return row(rate, "at_risk", notes.below ?? "Below the target range");
        if (rate > max) return row(rate, "at_risk", notes.above ?? "Above the target range");
        return row(rate, "on_track", null);
      }
      case "mean": {
        const values = valuesOf(def.key, from).map((o) => o.value ?? 0);
        const actual = mean(values);
        if (actual === null) return row(null, "on_track", notes.none ?? "Nothing measured yet");
        const below = values.filter((v) => !meets(v, target ?? 0)).length;
        return row(
          actual,
          below === 0 ? "on_track" : "at_risk",
          below === 0
            ? null
            : fill(notes.below ?? "{n} value(s) below {target}", { n: below, target: target ?? 0 }),
        );
      }
      case "latest": {
        const actual = valuesOf(def.key, null).at(-1)?.value ?? null;
        if (actual === null) return row(null, "at_risk", notes.none ?? "Nothing logged yet");
        const ok = meets(actual, target ?? 0);
        return row(actual, ok ? "on_track" : "missed", ok ? null : (notes.below ?? "Below target"));
      }
      case "baseline": {
        const b = spec.baseline;
        const { base, latest, ratio } = baselineOf(def);
        const reviewAt = b?.review_at ?? 0.9;
        const haltAt = b?.halt_at ?? 0.8;
        const floor = base === null ? null : Math.round(base * reviewAt);
        if (base === null)
          return row(latest, "at_risk", notes.none ?? `No ${b?.label ?? "baseline"}`, floor);
        if (ratio === null) return row(null, "at_risk", "Nothing logged yet", floor);
        const note = `${pct(ratio)} of the ${b?.label ?? "baseline"}`;
        if (ratio <= haltAt) return row(latest, "missed", `${note}: halt`, floor);
        if (ratio <= reviewAt) return row(latest, "at_risk", `${note}: review`, floor);
        return row(latest, "on_track", note, floor);
      }
      case "fanout": {
        // The mean over every active DE seat read (the totals), not over the rows shown.
        const total =
          spec.fanout === "founder" ? fanout.founder_minutes : fanout.supervisor_minutes;
        if (fanout.des === 0) return row(null, "on_track", "No active DE seats");
        const actual = total / fanout.des;
        return row(
          Math.round(actual),
          target === null || meets(actual, target) ? "on_track" : "at_risk",
          `Mean over ${fanout.des} active DE seat${fanout.des === 1 ? "" : "s"}`,
        );
      }
    }
    return row(null, "on_track", null);
  });

  // Review #10: a deadline criterion counts what was observed through its deadline; rows
  // logged after it can neither rescue nor sink it. Null when the observations are incomplete.
  const kill_criteria: GatePayload["kill_criteria"] = [];
  for (const def of defs) {
    const from = def.spec.count_from ?? null;
    for (const k of def.spec.kill ?? [])
      kill_criteria.push({
        text: k.text,
        triggered: obsGap ? null : asOf > k.due_on && sum(def.key, from, k.due_on) < k.below,
      });
    const b = def.spec.baseline;
    if (def.spec.measure === "baseline" && b) {
      const { ratio } = baselineOf(def);
      const below = (limit: number) => (obsGap ? null : ratio !== null && ratio <= limit);
      if (b.review_text) kill_criteria.push({ text: b.review_text, triggered: below(b.review_at) });
      if (b.halt_text) kill_criteria.push({ text: b.halt_text, triggered: below(b.halt_at) });
    }
  }

  const incomplete_reasons = [obsGap, fanGap].filter((r): r is string => !!r);
  return {
    as_of: asOf,
    company: input.company ?? null,
    complete: incomplete_reasons.length === 0,
    incomplete_reasons,
    gates,
    kill_criteria,
    per_de: [...input.perDe],
  };
}
